import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@cr/shared';
import { api } from '@/lib/api';
import { localized, money } from '@/lib/format';
import { haptic } from '@/lib/telegram';
import { useSession } from '@/lib/session';
import Icon from './icon';

export interface ProductSummary {
  id: string;
  name: LocalizedText;
  code: string;
  pricePerDay: number;
  /** Старая цена: если задана — рисуем перечёркнутой рядом с текущей (N4) */
  oldPrice: number | null;
  depositAmount: number | null;
  brand: { id: string; name: string } | null;
  isNew?: boolean;
  isPopular?: boolean;
  image: { thumbUrl: string; mediumUrl: string } | null;
}

export function ProductCard({
  product,
  favorite,
  onToggleFavorite,
}: {
  product: ProductSummary;
  favorite?: boolean;
  onToggleFavorite?: (productId: string, next: boolean) => void;
}) {
  const { t } = useTranslation();
  const { authorized } = useSession();
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: async () =>
      (await api.post<{ favorite: boolean }>(`/favorites/${product.id}`)).data,
    onSuccess: (data) => {
      haptic.tap();
      onToggleFavorite?.(product.id, data.favorite);
      void queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  return (
    <div className="relative">
      <Link to={`/product/${product.id}`} className="group block active:opacity-80">
        <div className="relative aspect-[3/4] overflow-hidden rounded-card bg-line shadow-soft">
          {product.image ? (
            <img
              src={product.image.mediumUrl}
              alt=""
              loading="lazy"
              className="size-full object-cover transition duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="grid size-full place-items-center text-muted/40">
              <Icon name="shirt" size={44} stroke={1.2} />
            </div>
          )}

          {product.depositAmount !== null && (
            <span className="absolute left-2 top-2 rounded-pill bg-ink/80 px-2 py-0.5 text-[11px] font-medium text-white">
              {t('product.deposit')}
            </span>
          )}

          {product.oldPrice ? (
            <span className="absolute bottom-2 left-2 rounded-pill bg-error px-2 py-0.5 text-[11px] font-semibold text-white">
              −{discountPercent(product.pricePerDay, product.oldPrice)}%
            </span>
          ) : (
            (product.isNew || product.isPopular) && (
              <span className="absolute bottom-2 left-2 rounded-pill bg-gold px-2 py-0.5 text-[11px] font-semibold text-white">
                {product.isNew ? t('product.new') : t('product.popular')}
              </span>
            )
          )}
        </div>

        <div className="mt-2 px-0.5">
          <p className="line-clamp-2 font-display text-base leading-tight">
            {localized(product.name)}
          </p>
          {product.brand && <p className="truncate text-xs text-muted">{product.brand.name}</p>}
          <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-[15px] font-bold text-bordo">{money(product.pricePerDay)}</span>
            <span className="text-[11px] text-muted">/ {t('product.perDay')}</span>
            {product.oldPrice ? (
              <span className="text-xs text-muted line-through">{money(product.oldPrice)}</span>
            ) : null}
          </div>
        </div>
      </Link>

      {authorized && (
        <button
          type="button"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          aria-label={t('nav.favorites')}
          aria-pressed={favorite}
          className="absolute right-2 top-2 grid size-9 place-items-center rounded-full bg-surface/90 shadow-soft active:scale-90"
        >
          <Icon
            name="heart"
            size={18}
            filled={favorite}
            className={favorite ? 'text-bordo' : 'text-muted'}
          />
        </button>
      )}
    </div>
  );
}

export function ProductGrid({
  products,
  favorites,
  onToggleFavorite,
}: {
  products: ProductSummary[];
  favorites?: Set<string>;
  onToggleFavorite?: (productId: string, next: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          favorite={favorites?.has(product.id)}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  );
}

/** Размер витринной скидки — его показываем плашкой на обложке. */
function discountPercent(price: number, oldPrice: number): number {
  return Math.max(1, Math.round((1 - price / oldPrice) * 100));
}
